import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { recipesApi } from '@/api/recipes';
import { useAuth }    from '@/providers/AuthProvider';
import {
  PageHeader, Button, Badge, Spinner, Card, CardHeader, CardTitle, CardContent,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Tabs,
} from '@/components/ui';
import { ArrowLeft, Pencil, Thermometer } from 'lucide-react';
import { formatPct, splitTextSteps } from '@/lib/utils';
import { getRecipeClassification } from '@/lib/recipeTaxonomy';
import { useCurrencyFormatter } from '../../providers/AppSettingsProvider';
import { getTechnicalTooltipMap } from './technicalTooltips';
import RecipeMediaUpload from '@/components/RecipeMediaUpload';

export default function RecipeDetailPage() {
  const { t }       = useTranslation();
  const { id }      = useParams<{ id: string }>();
  const { isAdmin } = useAuth();
  const formatMoney = useCurrencyFormatter();
  const [activeTab, setActiveTab] = useState<'ingredients' | 'notes' | 'media'>('ingredients');

  const { data: recipe, isLoading } = useQuery({
    queryKey: ['recipe', id],
    queryFn:  () => recipesApi.get(id!),
    enabled:  !!id,
  });

  if (isLoading) return <Spinner className="py-20" />;
  if (!recipe)   return <p className="py-10 text-center text-muted-foreground">Recipe not found.</p>;

  const calc = recipe.calculations;
  const technicalTooltips = getTechnicalTooltipMap(t);
  const classification = getRecipeClassification(recipe, t);

  return (
    <div>
      <PageHeader title={recipe.name} subtitle={classification.length > 0 ? classification.join(' · ') : undefined}>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/recipes"><ArrowLeft size={15} /> {t('common.back')}</Link>
          </Button>
          {isAdmin && (
            <Button asChild size="sm">
              <Link to={`/recipes/${id}/edit`}><Pencil size={14} /> {t('common.edit')}</Link>
            </Button>
          )}
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left column: tabbed content */}
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <Tabs
              tabs={[
                { key: 'ingredients', label: t('recipes.ingredients') },
                { key: 'notes',       label: 'Notes' },
                { key: 'media',       label: t('media.tabLabel') },
              ]}
              active={activeTab}
              onChange={k => setActiveTab(k as typeof activeTab)}
              className="px-5"
            />
            <CardContent className="pt-5">

              {/* ── Ingredients tab ─────────────────────────────────────── */}
              {activeTab === 'ingredients' && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('ingredients.name')}</TableHead>
                      <TableHead className="text-right">{t('recipes.gramsPerKilo')}</TableHead>
                      <TableHead className="text-right">{t('recipes.wasteRatio')}</TableHead>
                      <TableHead className="text-right">{t('recipes.totalGrams')}</TableHead>
                      <TableHead className="text-right">{t('recipes.costContribution')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recipe.ingredients.map((line, idx) => {
                      const lc = calc?.line_calculations.find(l => l.ingredient_id === line.ingredient_id);
                      return (
                        <TableRow key={line.id ?? idx}>
                          <TableCell className="font-medium">{line.ingredient_name}</TableCell>
                          <TableCell className="text-right font-mono">{Number(line.grams_per_kilo).toFixed(2)}</TableCell>
                          <TableCell className="text-right">{(Number(line.waste_ratio) * 100).toFixed(1)}%</TableCell>
                          <TableCell className="text-right font-mono">{Number(line.total_grams).toFixed(2)}</TableCell>
                          <TableCell className="text-right font-mono">{lc ? formatMoney(lc.cost_contribution) : '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}

              {/* ── Notes tab ───────────────────────────────────────────── */}
              {activeTab === 'notes' && (
                <div className="space-y-5">
                  {recipe.allergens.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('recipes.allergens')}</p>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {recipe.allergens.map(allergen => (
                          <div key={allergen.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
                            <input type="checkbox" checked readOnly className="h-4 w-4 rounded border-input text-primary" />
                            <span>{allergen.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {recipe.preparation_notes && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t('recipes.preparationNotes')}</p>
                      <OrderedSteps text={recipe.preparation_notes} />
                    </div>
                  )}
                  {recipe.presentation_notes && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t('recipes.presentationNotes')}</p>
                      <p className="text-sm whitespace-pre-wrap">{recipe.presentation_notes}</p>
                    </div>
                  )}
                  {recipe.kitchen_notes && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t('recipes.kitchenNotes')}</p>
                      <p className="text-sm whitespace-pre-wrap">{recipe.kitchen_notes}</p>
                    </div>
                  )}
                  {!recipe.allergens.length && !recipe.preparation_notes && !recipe.presentation_notes && !recipe.kitchen_notes && (
                    <p className="text-sm text-muted-foreground text-center py-6">—</p>
                  )}
                </div>
              )}

              {/* ── Media tab ───────────────────────────────────────────── */}
              {activeTab === 'media' && (
                <RecipeMediaUpload
                  recipeId={id!}
                  canUpload={isAdmin}
                  canDelete={isAdmin}
                />
              )}

            </CardContent>
          </Card>
        </div>

        {/* Right column: summary */}
        {/* Right column: summary */}
        <div className="space-y-4">
          {calc && (
            <>
              {/* Production */}
              <Card>
                <CardHeader><CardTitle>{t('recipes.production')}</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <Row label={t('recipes.totalProduction')} value={`${calc.total_production_grams} g`} />
                  <Row label={t('recipes.portions')}         value={String(calc.number_of_portions)} />
                  <Row label={t('recipes.costPerPortion')}   value={formatMoney(calc.cost_per_portion)} bold />
                  <Row label={t('recipes.recommendedPrice')} value={formatMoney(calc.recommended_price)} />
                  {recipe.selling_price != null && (
                    <>
                      <Row label={t('recipes.netPrice')}     value={formatMoney(calc.net_selling_price)} />
                      <Row label={t('recipes.netProfit')}    value={formatMoney(calc.net_profit)} />
                      <Row label={t('recipes.actualMargin')} value={formatPct(calc.actual_margin_pct)} />
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Serving temp — gelato only */}
              {recipe.recipe_category === 'gelato' && (
                <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/30">
                  <CardContent className="flex items-center gap-3 p-4">
                    <Thermometer size={22} className="text-blue-500" />
                    <div>
                      <p className="text-xs text-muted-foreground">{t('recipes.servingTemp')}</p>
                      <p className="text-xl font-bold text-blue-700 dark:text-blue-300">{calc.suggested_serving_temp}</p>
                    </div>
                    <Badge className="ml-auto">{t('recipes.totalPAC')}: {calc.total_pac}</Badge>
                  </CardContent>
                </Card>
              )}

              {/* Technical balance */}
              <Card>
                <CardHeader><CardTitle>{t('recipes.technicals')}</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <Row label={t('recipes.totalSugars')}  value={formatPct(calc.total_sugars_pct)} tooltip={technicalTooltips.sugars} />
                  <Row label={t('recipes.totalFat')}     value={formatPct(calc.total_fat_pct)} tooltip={technicalTooltips.fat} />
                  <Row label={t('recipes.totalSML')}     value={formatPct(calc.total_sml_pct)} tooltip={technicalTooltips.sml} />
                  <Row label="Solids (non-fat)"          value={formatPct(calc.total_solids_diff)} />
                  <Row label={t('recipes.totalSolids')}  value={formatPct(calc.total_solids_total)} tooltip={technicalTooltips.totalSolids} />
                  {recipe.recipe_category === 'gelato' && <Row label={t('recipes.totalPOD')} value={String(calc.total_pod)} />}
                  {recipe.recipe_category === 'gelato' && <Row label={t('recipes.totalPAC')} value={String(calc.total_pac)} tooltip={technicalTooltips.pac} bold />}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function OrderedSteps({ text }: { text: string }) {
  const steps = splitTextSteps(text);

  if (steps.length === 0) {
    return <p className="text-sm whitespace-pre-wrap">{text}</p>;
  }

  return (
    <ol className="list-decimal space-y-2 pl-5 text-sm marker:font-semibold">
      {steps.map((step, index) => (
        <li key={`${index}-${step}`}>{step}</li>
      ))}
    </ol>
  );
}

function Row({ label, value, bold, tooltip }: { label: string; value: string; bold?: boolean; tooltip?: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={tooltip ? `cursor-help decoration-dotted underline-offset-4 ${bold ? 'font-semibold underline' : 'underline'}` : bold ? 'font-semibold' : ''}
        title={tooltip}
      >
        {value}
      </span>
    </div>
  );
}
